import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, FileText } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

export default function SiteHero() {
  const { t } = useLanguage();

  return (
    <section id="top" className="circuit-bg relative overflow-hidden bg-forest-900 pb-20 pt-28 md:pt-36" data-testid="hero-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-body text-[11px] font-semibold uppercase tracking-[0.3em] text-mint"
              data-testid="hero-kicker"
            >
              {t.hero.kicker}
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 font-display text-4xl font-bold leading-[1.08] tracking-tight text-cream sm:text-5xl lg:text-6xl"
              data-testid="hero-title"
            >
              {t.hero.h1a}
              <br />
              {t.hero.h1b}{" "}
              <em className="italic text-mint">{t.hero.h1c}</em>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-5 font-body text-xs font-semibold uppercase tracking-[0.25em] text-cream/60"
            >
              {t.hero.sub}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.28 }}
              className="mt-6 max-w-xl border-l-[3px] border-ember pl-5 text-sm leading-relaxed text-cream/75 md:text-base"
              data-testid="hero-desc"
            >
              {t.hero.desc}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.36 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <a
                href="#contacto"
                data-testid="hero-cta-demo"
                className="inline-flex items-center gap-2 bg-ember px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-orange-600"
              >
                {t.hero.ctaDemo} <ArrowRight size={17} />
              </a>
              <Link
                to="/documentos"
                data-testid="hero-cta-docs"
                className="inline-flex items-center gap-2 border border-cream/25 px-7 py-3.5 text-sm font-bold text-cream transition-colors hover:border-mint hover:text-mint"
              >
                <FileText size={17} /> {t.hero.ctaDocs}
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-10 flex flex-wrap gap-2.5"
              data-testid="hero-pills"
            >
              {t.hero.pills.map((pill) => (
                <span
                  key={pill}
                  className={`border px-3.5 py-1.5 font-body text-[10px] font-semibold uppercase tracking-[0.2em] ${
                    pill.includes("ZeroSight")
                      ? "border-mint/50 text-mint"
                      : "border-cream/25 text-cream/70"
                  }`}
                >
                  {pill}
                </span>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="relative hidden justify-center lg:flex"
          >
            <img
              src="/assets/sense-device-cover.jpg"
              alt="WasteData Sense — sensore Edge AI"
              className="device-blend max-h-[560px] w-auto object-contain"
              data-testid="hero-device-image"
            />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mx-auto mt-16 max-w-4xl md:mt-24"
        >
          <p className="kicker text-center" data-testid="video-kicker">{t.hero.videoKicker}</p>
          <h2 className="mt-3 text-center font-display text-2xl font-bold text-cream md:text-3xl">
            {t.hero.videoTitle}
          </h2>
          <div className="mt-8 border border-cream/15 bg-forest-950 p-2 shadow-2xl shadow-black/40" data-testid="video-embed-wrapper">
            <div className="aspect-video w-full">
              <iframe
                src="https://www.youtube.com/embed/vpU-jfsnfdU"
                title="WasteData — Autonomous Waste Intelligence for Cities"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
                className="h-full w-full"
                data-testid="youtube-embed"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
